import { RefObject, useEffect, useState } from 'react'
import { ensureFontLoaded } from 'lib/fontLoader'
import { CHAT_FONT_FAMILIES } from 'lib/chatFontCatalog'

const HOME_BODY_FONT_KEY = 'INTER'
const HOME_DISPLAY_FONT_KEY = 'BEBAS_NEUE'

const faceUsable = (family: string): boolean => {
  if (typeof globalThis.document === 'undefined') return true
  const fonts = globalThis.document.fonts
  if (fonts == null || typeof fonts.check !== 'function') return true
  try {
    return fonts.check(`400 16px "${family}"`)
  } catch {
    return false
  }
}

export const useHomeFontsReady = (): boolean => {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    let cancelled = false
    void Promise.all([
      ensureFontLoaded(HOME_BODY_FONT_KEY),
      ensureFontLoaded(HOME_DISPLAY_FONT_KEY)
    ]).then(() => {
      if (cancelled) return
      const usable = [HOME_BODY_FONT_KEY, HOME_DISPLAY_FONT_KEY]
        .every((key) => faceUsable(CHAT_FONT_FAMILIES[key].family))
      if (usable) setReady(true)
    })
    return () => { cancelled = true }
  }, [])
  return ready
}

export const useNearViewport = (
  ref: RefObject<Element | null>,
  { rootMargin = '200px', initial = false }: { rootMargin?: string, initial?: boolean } = {}
): boolean => {
  const [near, setNear] = useState(() => initial || typeof globalThis.IntersectionObserver === 'undefined')
  useEffect(() => {
    const node = ref.current
    const Observer = globalThis.IntersectionObserver
    if (node == null || typeof Observer === 'undefined') {
      setNear(true)
      return
    }
    const observer = new Observer((entries) => {
      const latest = entries[entries.length - 1]
      if (latest != null) setNear(latest.isIntersecting)
    }, { rootMargin })
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref, rootMargin])
  return near
}

export const useElementWidth = (ref: RefObject<Element | null>): number => {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = ref.current
    if (node == null) return
    const measure = (): void => setWidth(node.clientWidth)
    measure()
    const Observer = globalThis.ResizeObserver
    if (typeof Observer === 'undefined') return
    const observer = new Observer(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [ref])
  return width
}

const WALL_DESIGN_MIN = 480
const WALL_DESIGN_MAX = 1920
export const wallDesignSize = (frameWidth: number): { width: number, height: number } => {
  const raw = Number.isFinite(frameWidth) ? Math.round(frameWidth * 1.5) : WALL_DESIGN_MIN
  const width = Math.min(WALL_DESIGN_MAX, Math.max(WALL_DESIGN_MIN, raw))
  return { width, height: Math.round(width * 9 / 16) }
}
